<script setup lang="ts">
import {computed} from "vue";
import {joinURL, parseURL, withQuery} from "ufo";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  spotifyUrlToEmbed: string;
  spotifyUrl?: string;
  youtubeUrl?: string;
  applePodcastUrl?: string;
  themeColorSpotify?: boolean;
  compact?: boolean;
  asVideo?: boolean; // will only work for podcasts, will fall back to non-video podcasts
}>();

const _spotifyUrl = computed(() => {
  return props.spotifyUrl ?? props.spotifyUrlToEmbed;
});

const spotifyEmbedUrl = computed(() => {
  const pathname = parseURL(props.spotifyUrlToEmbed)
    .pathname.replace("/video", "")
    .replace("/embed", "");
  const isPodcast = pathname.includes("episode") || pathname.includes("show");
  const video = isPodcast && props.asVideo ? "video" : "";
  return withQuery(joinURL("https://open.spotify.com/embed", pathname, video), {
    theme: props.themeColorSpotify ? 1 : 0,
  });
});

const sizeClass = computed(() => {
  if (spotifyEmbedUrl.value.includes("video")) {
    return "aspect-video";
  }

  // Leaving this here for future reference, but in this case in the header as a promo
  // we don't want the embeds too large.
  // if (spotifyEmbedUrl.value.includes("playlist")) {
  //   return props.compact ? "h-[152px]" : "h-[352px]";
  // }

  // h-20 will work for a long time, but at some point we must use h-[152px] to not lose buttons
  return props.compact ? "h-[152px] @xs:h-20" : "h-[152px]";
});

const atLeastOneOther = computed(() => {
  return (
    props.youtubeUrl ||
    props.applePodcastUrl ||
    props.spotifyUrl ||
    _spotifyUrl.value != props.spotifyUrlToEmbed
  );
});
</script>

<template>
  <section
    class="flex-shrink-0 w-full @container max-w-[480px] gap-4 flex flex-col"
    :aria-label="$t('podcast.featured')">
    <iframe
      :src="spotifyEmbedUrl"
      frameBorder="0"
      allowfullscreen="true"
      allowtransparency="true"
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
      class="w-full col-span-full rounded-lg"
      :class="[sizeClass]"></iframe>
    <div
      class="flex flex-wrap gap-4 w-full items-center justify-end"
      v-if="atLeastOneOther">
      <span class="text-sm" aria-hidden="true">Listen on</span>
      <a
        v-if="_spotifyUrl"
        class="podcast-link"
        target="_blank"
        :href="_spotifyUrl">
        <UIcon name="ph:spotify-logo-fill"></UIcon>
        <span class="sr-only">{{ $t("podcast.listen") }} Spotify</span>
      </a>
      <a
        v-if="youtubeUrl"
        class="podcast-link"
        target="_blank"
        :href="youtubeUrl">
        <UIcon name="ph:youtube-logo-fill"></UIcon>
        <span class="sr-only">{{ $t("podcast.listen") }} YouTube</span>
      </a>
      <a
        v-if="applePodcastUrl"
        class="podcast-link"
        target="_blank"
        :href="applePodcastUrl">
        <UIcon name="ph:apple-podcasts-logo-fill"></UIcon>
        <span class="sr-only">{{ $t("podcast.listen") }} Apple Podcast</span>
      </a>
    </div>
  </section>
</template>

<style scoped lang="scss">
iframe {
  color-scheme: auto;
}
.podcast-link {
  @apply inline-flex aspect-square p-2 border border-white rounded-full transition-colors text-xl;
  @apply hover:bg-white hover:text-[--trimm-button-hover-color,rgb(35,31,32)];
}
</style>
